import jsPDF from "jspdf";
import 'jspdf-autotable';

// Add custom fonts and styles
const addCustomStyles = (doc) => {
  // Add custom fonts if needed
  // doc.addFont('path/to/font.ttf', 'FontName', 'normal');
  
  // Set default styles
  doc.setFont('helvetica');
  doc.setTextColor(51, 51, 51); // Dark gray text
};

// Draw header with left logo and right-aligned title
const drawHeader = (doc, companyType, companyLogo, userName = '') => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const logoY = 15;
  
  // Add company logo on the left with fixed dimensions
  if (companyLogo) {
    try {
      // Use fixed dimensions that work well with most logos
      const logoWidth = 40;
      const logoHeight = 15; // Fixed height to prevent stretching
      
      // Add logo with fixed dimensions
      doc.addImage({
        imageData: companyLogo,
        x: margin,
        y: logoY,
        width: logoWidth,
        height: logoHeight
      });
    } catch (error) {
      console.warn('Could not add logo to PDF, using text fallback:', error);
      // Fallback text if logo fails to load
      doc.setFontSize(12);
      doc.text('BooksComply', margin, logoY + 10);
    }
  }
  
  // Title on the right
  const titleX = pageWidth - margin;
  
  // Main title (reduced font size)
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 51, 102); // Dark blue
  doc.text("USA COMPANY FORMATION", titleX, logoY + 5, { align: 'right' });
  
  // Company type
  doc.setFontSize(11);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(102, 102, 102); // Medium gray
  const companyTypeY = logoY + 11;
  doc.text(`${companyType} Formation Checklist`, titleX, companyTypeY, { align: 'right' });
  
  // User name (ensured to be displayed)
  const userNameText = userName ? String(userName).trim() : '';
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100); // Darker gray for better visibility
  doc.text(
    `Prepared for: ${userNameText || 'N/A'}`, 
    titleX, 
    companyTypeY + 7, 
    { align: 'right' }
  );
  
  // Generation date
  doc.setFontSize(9);
  doc.text(`Generated: ${new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })}`, titleX, logoY + 28, { align: 'right' });
  
  // Divider line
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  const dividerY = logoY + 35;
  doc.line(margin, dividerY, pageWidth - margin, dividerY);
  
  return dividerY + 15; // Return Y position after header
};

// Draw section header
const drawSectionHeader = (doc, y, title) => {
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 51, 102); // Dark blue
  doc.text(title.toUpperCase(), 20, y);
  
  // Underline
  doc.setDrawColor(0, 102, 204); // Blue
  doc.setLineWidth(0.5);
  doc.line(20, y + 2, 50, y + 2);
  
  return y + 10;
};

// Draw a question with answer and highlighted comment
const drawQuestion = (doc, y, question, answer, comment) => {
  const margin = 25;
  const contentWidth = 160;
  
  // Question text
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(51, 51, 51); // Dark gray
  
  const questionLines = doc.splitTextToSize(question, contentWidth);
  doc.text(questionLines, margin, y);
  y += questionLines.length * 6 + 4;
  
  // Answer
  if (answer) {
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 100, 0); // Dark green
    
    const answerText = typeof answer === 'string' ? answer : JSON.stringify(answer);
    const answerLines = doc.splitTextToSize(`✓ ${answerText}`, contentWidth - 5);
    doc.text(answerLines, margin + 5, y);
    y += answerLines.length * 6 + 6;
    
    // Comment with highlight if exists
    if (comment) {
      const commentText = `Note: ${comment}`;
      const commentLines = doc.splitTextToSize(commentText, contentWidth - 10);
      const commentHeight = commentLines.length * 5 + 6;
      
      // Draw highlight background
      doc.setFillColor(255, 248, 220); // Light yellow background
      doc.roundedRect(margin, y - 2, contentWidth - 5, commentHeight + 2, 2, 2, 'F');
      
      // Draw comment text
      doc.setFont(undefined, 'italic');
      doc.setTextColor(139, 69, 19); // Brown text for better contrast
      doc.text(commentLines, margin + 5, y + 4);
      
      // Draw border around comment
      doc.setDrawColor(255, 215, 0); // Gold border
      doc.setLineWidth(0.3);
      doc.roundedRect(margin, y - 2, contentWidth - 5, commentHeight + 2, 2, 2, 'S');
      
      y += commentHeight + 8;
    } else {
      y += 2;
    }
  } else {
    doc.setFont(undefined, 'italic');
    doc.setTextColor(150, 150, 150); // Light gray
    doc.text('No answer provided', 30, y);
    y += 10;
  }
  
  // Divider line
  doc.setDrawColor(230, 230, 230);
  doc.setLineWidth(0.2);
  doc.line(25, y, 190, y);
  
  return y + 5;
};

// Add footer to each page
const addFooter = (doc, pageNumber, totalPages) => {
  const pageSize = doc.internal.pageSize;
  const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
  
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(150, 150, 150);
  
  // Footer line
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.line(20, pageHeight - 20, 190, pageHeight - 20);
  
  // Footer text
  doc.text('BooksComply - USA Company Formation', 20, pageHeight - 10);
  doc.text(`Page ${pageNumber} of ${totalPages}`, 190, pageHeight - 10, { align: 'right' });
};

export const generateUSAFormationPDF = (formQuestions, answers, comments, companyType, companyLogo, userName = '') => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });
  
  // Add custom styles
  addCustomStyles(doc);
  
  let yPosition = drawHeader(doc, companyType, companyLogo, userName);
  const startY = yPosition;
  
  // Process each section
  Object.entries(formQuestions).forEach(([sectionTitle, questions], sectionIndex) => {
    // Add new page if needed
    if (yPosition > 260) {
      doc.addPage();
      yPosition = 20;
      addFooter(doc, doc.internal.getNumberOfPages(), 0);
    }
    
    // Draw section header
    yPosition = drawSectionHeader(doc, yPosition, sectionTitle);
    
    // Process questions
    questions.forEach((question, questionIndex) => {
      const questionKey = `${sectionTitle}-${questionIndex}`;
      const answer = answers[questionKey];
      const comment = comments[questionKey];
      
      // Add new page if needed
      if (yPosition > 260) {
        doc.addPage();
        yPosition = 20;
        addFooter(doc, doc.internal.getNumberOfPages(), 0);
      }
      
      // Draw question with answer and comment
      yPosition = drawQuestion(doc, yPosition, question, answer, comment);
    });
    
    // Add extra space between sections
    yPosition += 10;
  });
  
  // Add page numbers to all pages
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    addFooter(doc, i, pageCount);
  }
  
  return doc.output('blob');
};
